import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Calendar, User as UserIcon, ShoppingBag, ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { NAV, SITE } from "../lib/site";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";

const PRIMARY_COUNT = 7;

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { user, login } = useAuth();
  const { count } = useCart();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-[background,box-shadow,padding] duration-500 ${
          scrolled ? "py-3" : "py-6"
        }`}
      >
        <div className="mx-auto max-w-[1720px] px-4 sm:px-8">
          <div
            className={`glass rounded-full flex items-center justify-between pl-6 pr-3 py-3 sm:pl-8 sm:pr-4 sm:py-4 gap-3 transition-all duration-500 ${
              scrolled ? "" : "bg-white/50"
            }`}
          >
            <Link
              to="/"
              data-testid="brand-home-link"
              className="flex flex-col items-start leading-none whitespace-nowrap"
            >
              <span className="font-display text-xl sm:text-2xl leading-none text-[hsl(var(--onyx))] tracking-tight">
                Malek Dridi
              </span>
              <span
                className="mt-1 text-[9px] sm:text-[10px] uppercase tracking-[0.28em] font-medium text-[hsl(var(--gold))]"
                data-testid="brand-subheading"
              >
                Hair &amp; Beauty
              </span>
            </Link>

            <nav className="hidden xl:flex items-center gap-0">
              {NAV.map((n, i) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-link-${n.to.replace(/\//g, "") || "home"}`}
                  className={({ isActive }) =>
                    `${i >= PRIMARY_COUNT ? "hidden min-[1800px]:inline-flex" : ""} px-2 py-2 rounded-full text-[13px] whitespace-nowrap font-medium transition-colors duration-300 ${
                      isActive
                        ? "text-[hsl(var(--onyx))] bg-[hsl(var(--blush)/0.55)]"
                        : "text-[hsl(var(--onyx)/0.65)] hover:text-[hsl(var(--onyx))]"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <DropdownMenu>
                <DropdownMenuTrigger
                  data-testid="nav-more-trigger"
                  className="min-[1800px]:hidden inline-flex items-center gap-1 px-2 py-2 rounded-full text-[13px] font-medium text-[hsl(var(--onyx)/0.65)] hover:text-[hsl(var(--onyx))] transition-colors duration-300 outline-none"
                >
                  More <ChevronDown size={13} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-2xl bg-[hsl(var(--cream))] p-2 min-w-[200px]">
                  {NAV.slice(PRIMARY_COUNT).map((n) => (
                    <DropdownMenuItem key={n.to} asChild className="rounded-xl cursor-pointer text-sm">
                      <Link to={n.to} data-testid={`nav-more-link-${n.to.replace(/\//g, "") || "home"}`}>{n.label}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            <div className="flex items-center gap-2">
              <Link
                to="/cart"
                data-testid="navbar-cart"
                aria-label="Cart"
                className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/60 border border-white/60 hover:bg-white transition-colors duration-300"
              >
                <ShoppingBag size={16} strokeWidth={1.6} />
                {count > 0 && (
                  <span
                    data-testid="navbar-cart-badge"
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] text-[10px] font-medium flex items-center justify-center px-1"
                  >
                    {count}
                  </span>
                )}
              </Link>
              {user ? (
                <Link
                  to="/account"
                  data-testid="navbar-account"
                  className="hidden sm:inline-flex items-center gap-2 rounded-full pl-1 pr-4 py-1 bg-white/60 border border-white/60 hover:bg-white transition-colors duration-300"
                >
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt=""
                      className="w-7 h-7 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-blush flex items-center justify-center text-xs">
                      {user.name?.[0]}
                    </div>
                  )}
                  <span className="text-xs font-medium text-[hsl(var(--onyx))]">
                    Account
                  </span>
                </Link>
              ) : (
                <button
                  onClick={login}
                  data-testid="navbar-signin"
                  className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-4 py-2 whitespace-nowrap text-xs font-medium text-[hsl(var(--onyx)/0.75)] hover:text-[hsl(var(--onyx))] transition-colors duration-300"
                >
                  <UserIcon size={14} /> Sign in
                </button>
              )}
              <a
                href={SITE.bookingUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="navbar-book-cta"
                className="btn-pill btn-primary !hidden sm:!inline-flex whitespace-nowrap"
              >
                <Calendar size={16} strokeWidth={1.5} />
                Book Appointment
              </a>
              <button
                data-testid="mobile-menu-toggle"
                aria-label="Toggle menu"
                onClick={() => setOpen((s) => !s)}
                className="xl:hidden w-11 h-11 rounded-full bg-[hsl(var(--onyx))] text-[hsl(var(--cream))] flex items-center justify-center transition-transform duration-300 hover:scale-105"
              >
                {open ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile sheet */}
      <div
        data-testid="mobile-nav-sheet"
        className={`fixed inset-0 z-40 xl:hidden transition-opacity duration-500 ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-[hsl(var(--onyx))]/40 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
        <div
          className={`absolute top-0 right-0 h-full w-[86%] max-w-sm overflow-y-auto bg-[hsl(var(--cream))] p-8 pt-24 shadow-2xl transition-transform duration-500 ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="overline mb-6">Navigation</div>
          <div className="flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${n.to.replace(/\//g, "") || "home"}`}
                className={({ isActive }) =>
                  `font-display text-3xl py-2 transition-colors duration-300 ${
                    isActive
                      ? "text-[hsl(var(--onyx))]"
                      : "text-[hsl(var(--onyx)/0.5)] hover:text-[hsl(var(--onyx))]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </div>
          <div className="mt-10">
            {user ? (
              <Link
                to="/account"
                onClick={() => setOpen(false)}
                data-testid="mobile-account"
                className="btn-pill btn-outline w-full mb-3"
              >
                <UserIcon size={16} /> My account
              </Link>
            ) : (
              <button
                onClick={() => { setOpen(false); login(); }}
                data-testid="mobile-signin"
                className="btn-pill btn-outline w-full mb-3"
              >
                <UserIcon size={16} /> Sign in
              </button>
            )}
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="mobile-book-cta"
              className="btn-pill btn-primary w-full"
            >
              <Calendar size={16} strokeWidth={1.5} /> Book Appointment
            </a>
          </div>
        </div>
      </div>
    </>
  );
};
